import { FileText, Inbox, Receipt } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import { formatMoney } from "../../timeTracking/utils";
import { PortalAPI } from "../api/portalApi";
import { refreshPortal } from "../queries";
import type { CompanyApproval, NeedsYou, NeedsYouRequest } from "../types";

const isRequest = (n: NeedsYou): n is NeedsYouRequest =>
  "kind" in n && n.kind === "case_request";

const run = async (action: () => Promise<unknown>, done: string) => {
  try {
    await action();
    toast(done);
    return true;
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Couldn't do that");
    return false;
  } finally {
    void refreshPortal();
  }
};

const Frame: FC<{
  icon: ReactNode;
  title: string;
  sub: string;
  children: ReactNode;
  testId: string;
}> = ({ icon, title, sub, children, testId }) => (
  <div
    className="flex flex-col gap-2.5 rounded-lg border border-border bg-background p-3.5"
    data-testid={testId}
  >
    <span className="flex gap-2.5">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-base font-medium">{title}</span>
        <span className="text-sm leading-4.5 text-muted-foreground">{sub}</span>
      </span>
    </span>
    <div className="pl-6.5">{children}</div>
  </div>
);

/** Approve, or decline with a reason the firm reads. */
const Decide: FC<{
  approveLabel: string;
  onApprove: () => Promise<boolean>;
  onDecline: (note: string) => Promise<boolean>;
  extra?: ReactNode;
}> = ({ approveLabel, onApprove, onDecline, extra }) => {
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (fn: () => Promise<boolean>) => {
    setBusy(true);
    await fn();
    setBusy(false);
  };
  if (declining) {
    return (
      <div className="flex flex-col gap-2">
        <Textarea
          rows={2}
          autoFocus
          value={note}
          aria-label="Why you're declining"
          placeholder="Tell the firm why, or what you need instead"
          onChange={(e) => setNote(e.currentTarget.value)}
          className="text-base"
        />
        <span className="flex gap-2">
          <Button
            size="xs"
            variant="destructive"
            disabled={!note.trim() || busy}
            onClick={() => void go(() => onDecline(note.trim()))}
          >
            Decline
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setDeclining(false)}>
            Cancel
          </Button>
        </span>
      </div>
    );
  }
  return (
    <span className="flex flex-wrap gap-2">
      {extra}
      <Button
        variant="primary"
        size="xs"
        disabled={busy}
        onClick={() => void go(onApprove)}
      >
        {approveLabel}
      </Button>
      <Button size="xs" variant="outline" onClick={() => setDeclining(true)}>
        Decline
      </Button>
    </span>
  );
};

const AttachmentButton: FC<{ approval: CompanyApproval }> = ({ approval }) => {
  const [text, setText] = useState<string | null>(null);
  const open = async () => {
    try {
      const a = await PortalAPI.approvalAttachment(approval.id);
      if (a.url) window.open(a.url, "_blank", "noopener");
      else if (a.text) setText(a.text);
      else toast("The firm hasn't attached the document yet");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't open the document"
      );
    }
  };
  return (
    <>
      <Button size="xs" variant="outline" onClick={() => void open()}>
        <FileText /> Open {approval.attachment?.name || "document"}
      </Button>
      <Dialog open={text !== null} onOpenChange={(o) => !o && setText(null)}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{approval.attachment?.name || "Document"}</DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap text-base leading-5">{text}</div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const NeedsYouCard: FC<{ item: NeedsYou }> = ({ item }) => {
  if (isRequest(item)) {
    const r = item.request;
    return (
      <Frame
        testId={`needs-request-${item.id}`}
        icon={<Inbox className="size-4 text-warn" />}
        title={item.summary}
        sub={[r.job_title, r.cost_center, r.notes].filter(Boolean).join(" · ")}
      >
        <Decide
          approveLabel="Approve and send to the firm"
          onApprove={() =>
            run(() => PortalAPI.approveRequest(item.id), "Approved and sent")
          }
          onDecline={(note) =>
            run(() => PortalAPI.declineRequest(item.id, note), "Declined")
          }
        />
      </Frame>
    );
  }
  const a = item;
  const cost = a.kind === "cost";
  return (
    <Frame
      testId={`needs-approval-${a.id}`}
      icon={
        cost ? (
          <Receipt className="size-4 text-warn" />
        ) : (
          <FileText className="size-4 text-primary" />
        )
      }
      title={
        cost
          ? `Approve ${
              a.amount != null ? formatMoney(a.amount) : "a cost"
            } for ${a.employee_name}'s ${a.case_label}`
          : `Review ${a.employee_name}'s ${a.attachment?.name || "document"}`
      }
      sub={[a.summary, a.rule_label].filter(Boolean).join(" ")}
    >
      <Decide
        approveLabel={
          cost && a.amount != null
            ? `Approve ${formatMoney(a.amount)}`
            : "Approve"
        }
        extra={a.attachment ? <AttachmentButton approval={a} /> : null}
        onApprove={() =>
          run(() => PortalAPI.answerApproval(a.id, "approve", ""), "Approved")
        }
        onDecline={(note) =>
          run(() => PortalAPI.answerApproval(a.id, "decline", note), "Declined")
        }
      />
    </Frame>
  );
};
